/**
 * The summary of one draft run, shared by the GatherBanner (the website) and the QuestionnairePanel (the client
 * questionnaire): the headline, the count lines, the conflicts and the cells that could not be read.
 * Plain words only: never a raw code, an answer, a sheet link or a run id.
 */
import type { GatherResponse, QuestionnaireRunSummary } from '@/features/brand-bible-wizard/types';

export type DraftRunResult = GatherResponse | QuestionnaireRunSummary;

const REASON_WORDS: Record<string, string> = {
  formula: "formulas aren't read, type the answer instead",
  bad_number: 'that is not a number',
  bad_date: 'that is not a date (use YYYY-MM-DD)',
  bad_yes_no: 'answer Yes or No',
  bad_hex: 'that is not a colour code like #0A76A8',
  bad_url: 'that is not a web address starting with http:// or https://',
  bad_pick: 'pick one of the listed choices',
  bad_text: 'a number or date where text was expected',
  too_long: 'the answer is too long',
  missing_required: 'this item needs its main answer first, so it was skipped',
};

const TABLE_WORDS: Record<string, string> = {
  bb_voice_profile: 'Voice',
  bb_visual_identity: 'Visual identity',
  bb_locations: 'Main location',
};

const FIELD_WORDS: Record<string, string> = {
  voice_summary: 'summary',
  person: 'we or I',
  customer_term: 'what you call customers',
  humor_level: 'humour',
  font_names: 'fonts',
  real_photos_only: 'real photos only',
  badge_usage_notes: 'badge notes',
  gbp_url: 'Google Business Profile link',
  service_radius_miles: 'service radius (miles)',
};

function fieldWords(field: string | null | undefined): string {
  if (!field) return 'answer';
  if (field.startsWith('color_tokens.')) return `${field.split('.')[1]} colour`;
  if (field.startsWith('hours.')) return `${field.split('.')[1]} hours`;
  return FIELD_WORDS[field] ?? field.replace(/_/g, ' ');
}

const text = (value: unknown): string =>
  Array.isArray(value) ? value.map(String).join(', ') : value === null || value === undefined ? '' : String(value);

const list = <T,>(value: T[] | null | undefined): T[] => (Array.isArray(value) ? value : []);
const count = (value: unknown): number => (typeof value === 'number' && Number.isFinite(value) ? value : 0);

export function DraftRunSummary({
  result,
  source,
  testIdPrefix = 'draft',
  showHeadline = true,
}: {
  result: DraftRunResult | null | undefined;
  source: { kind: 'site' | 'questionnaire'; label: string };
  testIdPrefix?: string;
  /** The GatherBanner hides the headline when the site could not be read at all. */
  showHeadline?: boolean;
}) {
  if (!result) return null;
  const questionnaire = source.kind === 'questionnaire';
  // Every list and count may be missing (an older server, deploy skew): a summary never throws.
  const totals = (result.totals ?? {}) as Partial<DraftRunResult['totals']>;
  const created = count(totals.created);
  const skipped = count(totals.skipped);
  const conflictCount = count(totals.conflicts);
  const ignored =
    list(result.sources as { kind?: string; ignored?: number }[]).find((s) => s?.kind === 'questionnaire')?.ignored ?? 0;
  const cells = list(result.rejected as { tab?: string; row?: number; reason?: string }[]).filter(
    (r) => !!r && typeof r.tab === 'string' && typeof r.row === 'number',
  );
  const conflicts = list(result.conflicts).filter((c) => !!c);
  const notWritten = list((result as { not_written?: { table?: string; field?: string | null; found?: unknown }[] }).not_written).filter(
    (n) => !!n,
  );
  return (
    <>
      {showHeadline && (
        <div data-testid={`${testIdPrefix}-summary`} className="text-[12.5px] font-semibold">
          {created > 0
            ? `Found ${created} ${created === 1 ? 'draft' : 'drafts'} from ${source.label}. Review them below.`
            : `No new drafts found ${questionnaire ? 'in' : 'on'} ${source.label}.`}
        </div>
      )}
      {questionnaire && skipped > 0 && (
        <div data-testid={`${testIdPrefix}-summary-skipped`} className="text-[12px] text-muted-foreground">
          {skipped} already filled, left as they are.
        </div>
      )}
      {questionnaire && conflictCount > 0 && (
        <div data-testid={`${testIdPrefix}-summary-conflicts`} className="text-[12px] text-muted-foreground">
          {conflictCount} {conflictCount === 1 ? 'answer differs' : 'answers differ'} from what you have.
        </div>
      )}
      {questionnaire && ignored > 0 && (
        <div data-testid={`${testIdPrefix}-summary-ignored`} className="text-[12px] text-muted-foreground">
          {ignored} {ignored === 1 ? 'row was' : 'rows were'} not recognised and ignored.
        </div>
      )}
      {conflicts.map((c, i) => (
        <div key={`${c.table}:${c.field}:${i}`} data-testid={`${testIdPrefix}-conflict`} className="text-[12px] text-muted-foreground">
          {questionnaire
            ? `${c.field}: the client wrote ${c.found ?? 'a different value'}, which differs from what you have.`
            : `${c.field}: found ${c.found ?? 'a different value'} on the site, which differs from what you have.`}
        </div>
      ))}
      {questionnaire && notWritten.length > 0 && (
        <div data-testid={`${testIdPrefix}-not-written`} className="flex flex-col gap-0.5 text-[12px] text-muted-foreground">
          <span className="font-semibold">Not added automatically, add by hand:</span>
          {notWritten.map((n, i) => (
            <div key={`${n.table}:${n.field}:${i}`} data-testid={`${testIdPrefix}-not-written-item`}>
              {`${TABLE_WORDS[n.table ?? ''] ?? 'Brand Bible'}, ${fieldWords(n.field)}: ${text(n.found)}`}
            </div>
          ))}
        </div>
      )}
      {questionnaire &&
        cells.map((r, i) => (
          <div key={`${r.tab}:${r.row}:${i}`} data-testid={`${testIdPrefix}-rejected`} className="text-[12px] text-muted-foreground">
            {`${r.tab} \u00b7 row ${r.row}: ${REASON_WORDS[r.reason ?? ''] ?? 'this cell could not be read'}.`}
          </div>
        ))}
    </>
  );
}
